<template>
  <div class="flex flex-col">
    <!-- Loading Spinner -->
    <LoadingSpinner :isLoading="isLoading || isLoadingExperts" />

    <!-- Header Section -->
    <DirectoryHeader
      :directoryLayoutsAvailable="directoryLayoutsAvailable"
      @createNew="openCreateNewDirectoryLayoutModal"
    />

    <!-- Error Message -->
    <ErrorDisplay :errorMessage="errorMessage" />

    <!-- Directory Layouts List -->
    <div v-if="directoryLayoutsAvailable" class="space-y-6 p-4">
      <DirectoryLayoutCard
        v-for="layout in directoryLayouts"
        :key="layout.id"
        :layout="layout"
        :expandedLayoutId="expandedLayoutId"
        :profiles="profiles"
        :approvedExperts="approvedExperts"
        :userStore="userStore"
        :fieldErrors="fieldErrors"
        :updateDirectoryLoading="updateDirectoryLoading"
        @showCode="showCodeModal"
        @toggleExpanded="toggleLayoutExpanded"
        @delete="deleteLayout"
        @update="updateLayout"
        @cancelEdit="cancelEdit"
        @updateLayoutName="updateLayoutName"
        @updateLayoutType="updateLayoutType"
        @updateLayoutStyle="updateLayoutStyle"
        @updateLayoutCosmetic="updateLayoutCosmetic"
        @updateLayoutHeader="updateLayoutHeader"
        @updateLayoutWebsite="updateLayoutWebsite"
        @updateLayoutSearchFilters="updateLayoutSearchFilters"
        @updateLayoutSelectedExperts="updateLayoutSelectedExperts"
        @updateLayoutRandomExperts="updateLayoutRandomExperts"
      />
    </div>

    <!-- No Layouts Available -->
    <EmptyState v-else @createNew="openCreateNewDirectoryLayoutModal" />

    <!-- Modals -->
    <DirectoryCodeModal
      :isVisible="isCodeModalVisible"
      :companyId="companyId"
      :programUuid="programUuid"
      :layoutId="selectedLayoutId"
      @closeCodeModal="isCodeModalVisible = false"
    />

    <CreateNewDirectoryLayoutModal
      :isVisible="isCreateNewDirectoryLayoutModalVisible"
      @close="isCreateNewDirectoryLayoutModalVisible = false"
      @createLayout="handleCreateLayout"
    />

    <DeleteConfirmationModal
      v-if="showDeleteConfirmation && selectedLayoutToDelete"
      :layoutId="selectedLayoutToDelete"
      :layoutName="selectedLayoutName"
      @deleted="handleLayoutDeleted"
      @close="cancelDelete"
    />
  </div>
</template>

<script lang="ts">
import { gql } from "@apollo/client/core";
import { useQuery, useMutation } from "@vue/apollo-composable";
import { defineComponent, onBeforeUnmount, onMounted, ref, watch } from "vue";
import DeleteConfirmationModal from "@/components/shared/DeleteConfirmationModal.vue";
import {
  DirectoryLayout,
  DirectoryLayoutResponse,
  ApprovedExpert,
} from "@/types/dashboard-types";
import { directoryProfiles } from "@/data/expert-program/directory-preview-profiles";
import { Profile } from "@/types/dashboard-types";
import { CreateLayoutData } from "@/types/directory-types";
import { useUserStore } from "@/stores/user";
import DirectoryCodeModal from "@/components/dashboard/programs/directory-and-widget/DirectoryCodeModal.vue";
import CreateNewDirectoryLayoutModal from "@/components/dashboard/programs/directory-and-widget/CreateNewDirectoryLayoutModal.vue";
import { getCurrentTimeZoneInfo } from "@/utils/dateAndTimeUtils";
import LoadingSpinner from "./LoadingSpinner.vue";
import DirectoryHeader from "./DirectoryHeader.vue";
import ErrorDisplay from "./ErrorDisplay.vue";
import DirectoryLayoutCard from "./DirectoryLayoutCard.vue";
import EmptyState from "./EmptyState.vue";

const GET_PROGRAM_DIRECTORY = gql`
  query getProgramDirectoryDetail($programId: ID!) {
    getProgramDirectoryDetail(programId: $programId) {
      id
      style
      uuid
      companyId
      programUuid
      viewCount
      websiteUrl
      layoutName
      layoutType
      selectedExperts
      selectRandomExperts
      showSearchFilters
      directoryCosmetic {
        fontFamily
        bold
        textColor
        backgroundColor
        fontSize
      }
      directoryHeaderCosmetic {
        headerText
        paragraphText
        fontFamily
        headerFontSize
        paragraphFontSize
        textColor
        backgroundColor
        bold
        isVisible
      }
    }
  }
`;

const LIST_PROGRAM_EXPERTS_QUERY = gql`
  query listProgramExperts(
    $programId: ID!
    $expertsType: String
    $attendeeTimezone: String!
  ) {
    listProgramExperts(programId: $programId, expertsType: $expertsType) {
      experts {
        id
        firstName
        lastName
        email
        title
        companyName
        profile_picture
        linkedin_profile
        country
        uuid
        slug
        meetingsCount
        spokenLanguages
        anyPastMeetings
        active
        calendarActive
        soonestAvailabilities(attendeeTimezone: $attendeeTimezone) {
          startTime
          endTime
        }
        userProgramProfiles(programId: $programId) {
          expertise
          experience
        }
      }
    }
  }
`;

const UPDATE_DIRECTORY = gql`
  mutation updateDirectory($input: UpdateDirectoryInput!) {
    updateDirectory(input: $input) {
      directory {
        id
        companyId
      }
      errors
    }
  }
`;

export default defineComponent({
  name: "DirectoryAndWidget",
  components: {
    LoadingSpinner,
    DirectoryHeader,
    ErrorDisplay,
    DirectoryLayoutCard,
    EmptyState,
    DirectoryCodeModal,
    CreateNewDirectoryLayoutModal,
    DeleteConfirmationModal,
  },
  props: {
    setErrorMessage: {
      type: Function,
      required: true,
    },
    setDirectoryViewCount: {
      type: Function,
      required: true,
    },
  },
  emits: ["hideWidgets"],
  setup(props, { emit }) {
    const userStore = useUserStore();
    const viewCount = ref(0);
    const profiles = ref<Profile[]>(directoryProfiles.slice(0, 6));
    const directoryId = ref("");
    const companyId = ref("");
    const programUuid = ref("");
    const errorMessage = ref("");
    const directoryLayoutsAvailable = ref(false);
    const isCreateNewDirectoryLayoutModalVisible = ref(false);
    const expandedLayoutId = ref<string | null>(null);
    const directoryLayouts = ref<DirectoryLayout[]>([]);
    const approvedExperts = ref<ApprovedExpert[]>([]);
    const isLoadingExperts = ref(false);
    const showDeleteConfirmation = ref(false);
    const selectedLayoutToDelete = ref<string | null>(null);
    const selectedLayoutName = ref<string>("");
    const fieldErrors = ref<Record<string, string>>({});
    const isCodeModalVisible = ref(false);

    const {
      result: directoryResult,
      error: directoryError,
      refetch: refetchDirectory,
      loading: isLoading,
    } = useQuery(
      GET_PROGRAM_DIRECTORY,
      {
        programId: userStore.selectedProgram?.id,
      },
      { errorPolicy: "all" }
    );

    const {
      mutate: updateDirectory,
      onDone: updateDirectoryDone,
      onError: updateDirectoryError,
      loading: updateDirectoryLoading,
    } = useMutation(UPDATE_DIRECTORY);

    const handleErrorMessage = (error: string) => {
      errorMessage.value = error;
      setTimeout(() => {
        errorMessage.value = "";
      }, 9200);
    };

    const handleDirectoryUpdated = () => {
      refetchDirectory();
    };

    onMounted(() => {
      window.addEventListener("resize", () => {
        // Handle resize if needed
      });
    });

    onBeforeUnmount(() => {
      window.removeEventListener("resize", () => {
        // Handle resize if needed
      });
    });

    // Add a ref to track if we're creating a new layout
    const isCreatingNewLayout = ref(false);

    watch(
      () => directoryResult.value,
      (newValue) => {
        if (newValue) {
          if (newValue.getProgramDirectoryDetail) {
            directoryLayoutsAvailable.value = true;
            // Handle array of directory layouts
            const layouts: DirectoryLayout[] =
              newValue.getProgramDirectoryDetail;
            if (layouts && layouts.length > 0) {
              // Create a deep copy of layouts to make them mutable
              const mutableLayouts = layouts.map((layout) => {
                // Ensure selectedExperts is an array
                const selectedExperts = Array.isArray(layout.selectedExperts)
                  ? layout.selectedExperts
                  : [];
                return {
                  ...layout,
                  directoryCosmetic: { ...layout.directoryCosmetic },
                  directoryHeaderCosmetic: {
                    ...layout.directoryHeaderCosmetic,
                  },
                  // Ensure both selectedExperts and selectedExpertIds are set to the same value
                  selectedExpertIds: selectedExperts,
                  selectedExperts: selectedExperts,
                };
              });
              directoryLayouts.value = mutableLayouts;

              // Only set expandedLayoutId if we're creating a new layout
              if (isCreatingNewLayout.value) {
                expandedLayoutId.value =
                  mutableLayouts[mutableLayouts.length - 1].id;
                isCreatingNewLayout.value = false; // Reset the flag
              }

              // Set other values
              const firstLayout = mutableLayouts[0];
              companyId.value = firstLayout.companyId;
              programUuid.value = firstLayout.programUuid;
              directoryId.value = firstLayout.id;
              // Calculate total view count across all layouts
              const totalViewCount = mutableLayouts.reduce(
                (sum, layout) => sum + (layout.viewCount || 0),
                0
              );
              viewCount.value = totalViewCount;
              props.setDirectoryViewCount(viewCount.value);
            }
          } else {
            directoryLayoutsAvailable.value = false;
            directoryLayouts.value = [];
          }
        }
      },
      { immediate: true }
    );

    watch(
      directoryError,
      (newValue) => {
        if (newValue) {
          props.setErrorMessage(newValue.message);
        }
      },
      { immediate: true }
    );

    refetchDirectory();

    const selectedLayoutId = ref<string>("");

    const showCodeModal = (layoutId: string) => {
      selectedLayoutId.value = layoutId;
      isCodeModalVisible.value = true;
    };

    const openCreateNewDirectoryLayoutModal = () => {
      isCreateNewDirectoryLayoutModalVisible.value = true;
    };

    const handleCreateLayout = (newLayout: CreateLayoutData) => {
      isLoading.value = true;
      isCreatingNewLayout.value = true; // Set flag before creating

      // Create a new layout with default values
      const defaultCosmetic = {
        fontFamily: "Lato",
        bold: "400",
        textColor: "#FFFFFF",
        backgroundColor: "#121111",
        fontSize: "14",
      };

      const defaultHeaderCosmetic = {
        fontFamily: "Lato",
        headerFontSize: "24",
        paragraphFontSize: "16",
        textColor: "#000000",
        backgroundColor: "#FFFFFF",
        bold: "400",
        headerText: "Our Experts",
        paragraphText: "Connect with our team of experts",
        isVisible: true,
      };

      updateDirectory({
        input: {
          directoryParams: {
            programId: userStore.selectedProgram?.id,
            layoutName: newLayout.layoutName,
            layoutType: newLayout.layoutType,
            style: "template-1", // Default template
            fontFamily: defaultCosmetic.fontFamily,
            bold: defaultCosmetic.bold,
            textColor: defaultCosmetic.textColor,
            backgroundColor: defaultCosmetic.backgroundColor,
            fontSize: defaultCosmetic.fontSize,
            textAlign: "left",
            websiteUrl: userStore.companyWebsite || "",
            selectedExperts: [],
            showSearchFilters: newLayout.layoutType === "grid",
            selectRandomExperts: false,
            headerCosmetic: defaultHeaderCosmetic,
          },
        },
      });

      updateDirectoryDone((response) => {
        if (response) {
          isLoading.value = false;
          if (response.data.updateDirectory.errors.length > 0) {
            handleErrorMessage(response.data.updateDirectory.errors.join("."));
          } else {
            isCreateNewDirectoryLayoutModalVisible.value = false;
            refetchDirectory();
          }
        }
      });

      updateDirectoryError((error) => {
        if (error) {
          isLoading.value = false;
          handleErrorMessage(error.message);
        }
      });
    };

    const toggleLayoutExpanded = (layoutId: string) => {
      expandedLayoutId.value =
        layoutId === expandedLayoutId.value ? null : layoutId;
    };

    const updateLayout = (layout: DirectoryLayout) => {
      isLoading.value = true;
      // Ensure selectedExperts is an array
      const selectedExperts = Array.isArray(layout.selectedExpertIds)
        ? layout.selectedExpertIds
        : [];

      updateDirectory({
        input: {
          directoryParams: {
            directoryId: layout.id,
            programId: userStore.selectedProgram?.id,
            layoutName: layout.layoutName || `Layout ${layout.id}`,
            layoutType: layout.layoutType || "grid",
            style: layout.style,
            fontFamily: layout.directoryCosmetic.fontFamily,
            bold: layout.directoryCosmetic.bold,
            textColor: layout.directoryCosmetic.textColor,
            backgroundColor: layout.directoryCosmetic.backgroundColor,
            fontSize: layout.directoryCosmetic.fontSize,
            textAlign: "left",
            websiteUrl: layout.websiteUrl,
            // Ensure we send both fields with the same value
            selectedExperts: selectedExperts,
            showSearchFilters: layout.showSearchFilters,
            selectRandomExperts: layout.selectRandomExperts,
            headerCosmetic: {
              fontFamily: layout.directoryHeaderCosmetic.fontFamily,
              headerFontSize: layout.directoryHeaderCosmetic.headerFontSize,
              paragraphFontSize:
                layout.directoryHeaderCosmetic.paragraphFontSize,
              textColor: layout.directoryHeaderCosmetic.textColor,
              bold: layout.directoryHeaderCosmetic.bold,
              backgroundColor: layout.directoryHeaderCosmetic.backgroundColor,
              headerText: layout.directoryHeaderCosmetic.headerText,
              paragraphText: layout.directoryHeaderCosmetic.paragraphText,
              isVisible: layout.directoryHeaderCosmetic.isVisible,
            },
          },
        },
      });

      updateDirectoryDone((response) => {
        if (response) {
          isLoading.value = false;
          if (response.data.updateDirectory.errors.length > 0) {
            handleErrorMessage(response.data.updateDirectory.errors.join("."));
          } else {
            expandedLayoutId.value = null;
            refetchDirectory();
          }
        }
      });

      updateDirectoryError((error) => {
        if (error) {
          isLoading.value = false;
          handleErrorMessage(error.message);
        }
      });
    };

    const updateLayoutStyle = (layoutId: string, template: string) => {
      const layoutIndex = directoryLayouts.value.findIndex(
        (l) => l.id === layoutId
      );
      if (layoutIndex !== -1) {
        // Create a new object to ensure reactivity
        const updatedLayout = { ...directoryLayouts.value[layoutIndex] };
        updatedLayout.style = template;
        directoryLayouts.value[layoutIndex] = updatedLayout;
      }
    };

    const updateLayoutCosmetic = (
      layoutId: string,
      property: string,
      value: any
    ) => {
      const layoutIndex = directoryLayouts.value.findIndex(
        (l) => l.id === layoutId
      );
      if (layoutIndex !== -1) {
        // Create a new object to ensure reactivity
        const updatedLayout = { ...directoryLayouts.value[layoutIndex] };
        updatedLayout.directoryCosmetic = {
          ...updatedLayout.directoryCosmetic,
        };
        (updatedLayout.directoryCosmetic as any)[property] = value;
        directoryLayouts.value[layoutIndex] = updatedLayout;
      }
    };

    const updateLayoutHeader = (
      layoutId: string,
      property: string,
      value: any
    ) => {
      const layoutIndex = directoryLayouts.value.findIndex(
        (l) => l.id === layoutId
      );
      if (layoutIndex !== -1) {
        // Create a new object to ensure reactivity
        const updatedLayout = { ...directoryLayouts.value[layoutIndex] };
        updatedLayout.directoryHeaderCosmetic = {
          ...updatedLayout.directoryHeaderCosmetic,
        };
        (updatedLayout.directoryHeaderCosmetic as any)[property] = value;
        directoryLayouts.value[layoutIndex] = updatedLayout;
      }
    };

    const updateLayoutWebsite = (layoutId: string, websiteUrl: string) => {
      const layoutIndex = directoryLayouts.value.findIndex(
        (l) => l.id === layoutId
      );
      if (layoutIndex !== -1) {
        // Create a new object to ensure reactivity
        const updatedLayout = { ...directoryLayouts.value[layoutIndex] };
        updatedLayout.websiteUrl = websiteUrl;
        directoryLayouts.value[layoutIndex] = updatedLayout;
      }
    };

    const updateLayoutSearchFilters = (layoutId: string, enabled: boolean) => {
      const layoutIndex = directoryLayouts.value.findIndex(
        (l) => l.id === layoutId
      );
      if (layoutIndex !== -1) {
        // Create a new object to ensure reactivity
        const updatedLayout = { ...directoryLayouts.value[layoutIndex] };
        updatedLayout.showSearchFilters = enabled;
        directoryLayouts.value[layoutIndex] = updatedLayout;
      }
    };

    const updateLayoutName = (layoutId: string, layoutName: string) => {
      const layoutIndex = directoryLayouts.value.findIndex(
        (l) => l.id === layoutId
      );
      if (layoutIndex !== -1) {
        // Create a new object to ensure reactivity
        const updatedLayout = { ...directoryLayouts.value[layoutIndex] };
        updatedLayout.layoutName = layoutName;
        directoryLayouts.value[layoutIndex] = updatedLayout;
      }
    };

    const updateLayoutType = (layoutId: string, layoutType: string) => {
      const layoutIndex = directoryLayouts.value.findIndex(
        (l) => l.id === layoutId
      );
      if (layoutIndex !== -1) {
        // Create a new object to ensure reactivity
        const updatedLayout = { ...directoryLayouts.value[layoutIndex] };
        updatedLayout.layoutType = layoutType;

        // When layout type is single or row, automatically set search filters to false
        if (layoutType === "single" || layoutType === "row") {
          updatedLayout.showSearchFilters = false;
        } else {
          updatedLayout.showSearchFilters = true;
        }

        directoryLayouts.value[layoutIndex] = updatedLayout;
      }
    };

    const cancelEdit = () => {
      expandedLayoutId.value = null;
    };

    const fetchExperts = () => {
      isLoadingExperts.value = true;
      const { onResult: onResultExperts, onError: onErrorExperts } = useQuery(
        LIST_PROGRAM_EXPERTS_QUERY,
        {
          programId: userStore.selectedProgram?.id,
          expertsType: "approved",
          attendeeTimezone: getCurrentTimeZoneInfo().timeZone,
        },
        {
          fetchPolicy: "network-only",
        }
      );

      onResultExperts((result) => {
        if (result) {
          isLoadingExperts.value = false;
          if (result.data) {
            // Filter experts to only include those with calendarActive true
            approvedExperts.value = result.data.listProgramExperts.experts
              .filter((expert: ApprovedExpert) => {
                return expert.calendarActive && expert.active;
              })
              .map((expert: ApprovedExpert) => {
                return {
                  ...expert,
                  // Ensure userProgramProfiles is always an array
                  userProgramProfiles: expert.userProgramProfiles || [],
                };
              });
          }
        }
      });

      onErrorExperts((error) => {
        if (error) {
          isLoadingExperts.value = false;
          handleErrorMessage(error.message);
        }
      });
    };

    fetchExperts();

    // Update layout selected experts with duplicate check
    function updateLayoutSelectedExperts(
      layoutId: string,
      ids: string[],
      fieldIndex?: number
    ): void {
      const layoutIndex = directoryLayouts.value.findIndex(
        (l: DirectoryLayout) => l.id === layoutId
      );
      if (layoutIndex !== -1) {
        const layout = directoryLayouts.value[layoutIndex];
        const currentSelectedIds = layout.selectedExpertIds || [];
        const fieldKey = `${layoutId}-${fieldIndex || 0}`;

        // Clear any existing errors for this field
        delete fieldErrors.value[fieldKey];

        // If ids is empty, it means clear all - allow it
        if (ids.length === 0) {
          const updatedLayout: DirectoryLayout = {
            ...layout,
          };
          updatedLayout.selectedExpertIds = [];
          updatedLayout.selectedExperts = [];
          directoryLayouts.value[layoutIndex] = updatedLayout;
          return;
        }

        // For row layout, only check if the newly selected expert at the specific field index is already selected elsewhere
        if (layout.layoutType === "row" && fieldIndex !== undefined) {
          const newExpertId = ids[fieldIndex];
          if (newExpertId && newExpertId !== "") {
            // Check if this expert is already selected in other positions (not the current field index)
            const isDuplicate = currentSelectedIds.some(
              (id, index) => id === newExpertId && index !== fieldIndex
            );

            if (isDuplicate) {
              // Get expert name for the message
              const duplicateExpert = approvedExperts.value.find(
                (e) => e.id === newExpertId
              );
              const expertName = duplicateExpert
                ? `${duplicateExpert.firstName} ${duplicateExpert.lastName}`
                : "This expert";

              // Set error for specific field
              fieldErrors.value[
                fieldKey
              ] = `${expertName} has already been added to the selected experts.`;

              // Clear error after 2 seconds
              setTimeout(() => {
                delete fieldErrors.value[fieldKey];
              }, 2000);

              return;
            }
          }
        } else if (layout.layoutType === "single") {
          // For single layout, check if the expert is already selected
          const newExpertId = ids[0];
          if (newExpertId && currentSelectedIds.includes(newExpertId)) {
            // Get expert name for the message
            const duplicateExpert = approvedExperts.value.find(
              (e) => e.id === newExpertId
            );
            const expertName = duplicateExpert
              ? `${duplicateExpert.firstName} ${duplicateExpert.lastName}`
              : "This expert";

            // Set error for specific field
            fieldErrors.value[
              fieldKey
            ] = `${expertName} has already been added to the selected experts.`;

            // Clear error after 2 seconds
            setTimeout(() => {
              delete fieldErrors.value[fieldKey];
            }, 2000);

            return;
          }
        }

        const updatedLayout: DirectoryLayout = {
          ...layout,
        };
        // For single expert selection, replace the array
        // For row layout, this handles individual slot updates
        updatedLayout.selectedExpertIds = ids;
        updatedLayout.selectedExperts = ids;
        directoryLayouts.value[layoutIndex] = updatedLayout;
      }
    }

    // Add function to handle random mode changes
    const updateLayoutRandomExperts = (
      layoutId: string,
      isRandom: boolean
    ): void => {
      const layoutIndex = directoryLayouts.value.findIndex(
        (l: DirectoryLayout) => l.id === layoutId
      );
      if (layoutIndex !== -1) {
        const updatedLayout: DirectoryLayout = {
          ...directoryLayouts.value[layoutIndex],
        };
        updatedLayout.selectRandomExperts = isRandom;
        // Clear selected experts when random mode is enabled
        if (isRandom) {
          updatedLayout.selectedExpertIds = [];
          updatedLayout.selectedExperts = [];
        }
        directoryLayouts.value[layoutIndex] = updatedLayout;
      }
    };

    const deleteLayout = (layoutId: string): void => {
      const layout = directoryLayouts.value.find((l) => l.id === layoutId);
      if (layout) {
        selectedLayoutToDelete.value = layoutId;
        selectedLayoutName.value = layout.layoutName || `Layout ${layout.id}`;
        showDeleteConfirmation.value = true;
      }
    };

    const handleLayoutDeleted = (): void => {
      showDeleteConfirmation.value = false;
      selectedLayoutToDelete.value = null;
      selectedLayoutName.value = "";
      expandedLayoutId.value = null;
      refetchDirectory();
    };

    const cancelDelete = (): void => {
      showDeleteConfirmation.value = false;
      selectedLayoutToDelete.value = null;
      selectedLayoutName.value = "";
    };

    return {
      handleDirectoryUpdated,
      updateDirectoryLoading,
      profiles,
      errorMessage,
      isLoading,
      isLoadingExperts,
      directoryLayoutsAvailable,
      directoryLayouts,
      approvedExperts,
      expandedLayoutId,
      fieldErrors,
      selectedLayoutId,
      showDeleteConfirmation,
      selectedLayoutToDelete,
      selectedLayoutName,
      isCreateNewDirectoryLayoutModalVisible,
      isCodeModalVisible,
      companyId,
      programUuid,
      userStore,
      showCodeModal,
      openCreateNewDirectoryLayoutModal,
      handleCreateLayout,
      toggleLayoutExpanded,
      updateLayout,
      updateLayoutStyle,
      updateLayoutCosmetic,
      updateLayoutHeader,
      updateLayoutWebsite,
      updateLayoutName,
      updateLayoutType,
      cancelEdit,
      updateLayoutSelectedExperts,
      updateLayoutSearchFilters,
      updateLayoutRandomExperts,
      deleteLayout,
      handleLayoutDeleted,
      cancelDelete,
    };
  },
});
</script>
